import { useMutation, useQuery } from "react-query";
import { getContentById } from "../../../../services/content.service";
import { WebinarContent } from "../../../../@types";
import { useLocation } from "react-router-dom";
import { AuthorsDisplay } from "../../home/content/components";
import { Article } from "../../home/content/article";
import { useTranslation } from "react-i18next";
import { formatDateRange } from "../../../../utils";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../../contexts/AuthContext";
import {
  addToWaitingList,
  cancelWebinar,
  isUserOnWaitingList,
  registerWebinar,
  removeFromWaitingList,
} from "../../../../services/webinar.service";
import { CREDITS_ERRORS } from "../../../../errors/errorCodes";
import Button from "../../../../components/button";
import { ButtonVariant } from "../../../../components/button/types";
import { CircularProgress } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

export function Webinar() {
  const location = useLocation();
  const { t } = useTranslation("community");
  const { user } = useContext(AuthContext);

  const id =
    location.pathname.split("/")[location.pathname.split("/").length - 1];
  const {
    data: webinar,
    isLoading,
    refetch: refetchWebinar,
  } = useQuery(["webinar", location], () =>
    getContentById({ platformContentId: id })
  );

  const [canRegister, setCanRegister] = useState(false);
  const [isCanceled, setIsCanceled] = useState(false);
  const [canCancel, setCanCancel] = useState(false);

  const [registerError, setRegisterError] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [buttonText, setButtonText] = useState<string | null>("");
  const getUserTimeZone = () =>
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [waitListButtonloading, setWaitListButtonloading] = useState(false);

  const [maxParticipantsReached, setMaxParticipantsReached] = useState(false);
  const [canBeAddedToWaitlist, setCanBeAddedToWaitlist] = useState(false);
  const [waitingListButtonText, setwaitingListButtonText] = useState<
    string | null
  >("");

  const { data: isOnWaitList, refetch: refetchWaitList } = useQuery(
    ["waitList", id],
    () => isUserOnWaitingList(id)
  );

  useEffect(() => {
    if (!isLoading) {
      setCanBeAddedToWaitlist(!isOnWaitList);
      setwaitingListButtonText(
        !isOnWaitList
          ? t("add-to-waitList-button-text")
          : t("remove-from-waitList-button-text")
      );
    }
  }, [isOnWaitList, isLoading]);

  const registerMutation = useMutation(
    (mutationArgs: {
      webinarId: string;
      webinarCost: number;
      userTimeZone: string;
    }) =>
      registerWebinar({
        webinarId: mutationArgs.webinarId,
        webinarCost: mutationArgs.webinarCost,
        userTimeZone: mutationArgs.userTimeZone,
      }),
    {
      onSuccess: async () => {
        setSuccessMessage(t("webinar-success"));
        refetchWebinar();
      },
      onError: ({ response }) => {
        if (response.data.message == CREDITS_ERRORS.INSUFFICIENT_CREDITS) {
          setRegisterError(t("errors.insufficient-credits"));
        } else if (response.data.message == CREDITS_ERRORS.WEBINAR_NOT_FOUND) {
          setRegisterError(t("errors.not-found-webinar"));
        } else {
          console.error("register webinar error:", response);
        }
      },
    }
  );

  const cancelMutation = useMutation(
    ["cancel"],
    (mutationArgs: { webinarId: string; userTimeZone: string }) =>
      cancelWebinar({
        webinarId: mutationArgs.webinarId,
        userTimeZone: mutationArgs.userTimeZone,
      }),
    {
      onSuccess: async () => {
        setSuccessMessage("");
        refetchWebinar();
      },
      onError: ({ response }) => {
        if (response.data.message == CREDITS_ERRORS.WEBINAR_NOT_FOUND) {
          setCancelError(t("errors.not-found-webinar"));
        } else {
          console.error("register webinar error:", response);
        }
      },
    }
  );

  const addToWaitListMutation = useMutation(
    ["addToWaitList"],
    (mutationArgs: { contentId: string }) =>
      addToWaitingList({
        contentId: mutationArgs.contentId,
      }),
    {
      onSuccess: async () => {
        setSuccessMessage(t("added-to-waitList-success"));
        setwaitingListButtonText(t("add-to-waitList-button-text"));
        refetchWebinar();
        refetchWaitList();
        setWaitListButtonloading(false);
      },
      onError: ({ response }) => {
        if (response.data.message == CREDITS_ERRORS.WEBINAR_NOT_FOUND) {
          setCancelError(t("errors.not-found-webinar"));
        } else {
          console.error("add to waitlist webinar error:", response);
        }
      },
    }
  );

  const removeFromWaitListMutation = useMutation(
    ["removeFromWaitList"],
    (mutationArgs: { contentId: string }) =>
      removeFromWaitingList({
        contentId: mutationArgs.contentId,
      }),
    {
      onSuccess: async () => {
        setSuccessMessage(t("removed-from-waitList-success"));
        setwaitingListButtonText(t("remove-from-waitList-button-text"));
        refetchWebinar();
        refetchWaitList();
        setWaitListButtonloading(false);
      },
      onError: ({ response }) => {
        if (response.data.message == CREDITS_ERRORS.WEBINAR_NOT_FOUND) {
          setCancelError(t("errors.not-found-webinar"));
        } else {
          console.error("removed from waitlist webinar error:", response);
        }
      },
    }
  );

  const handleRegisterClick = (webinar: any) => {
    const webinarId = String(webinar.id);
    const webinarCost = Number(webinar.credits);
    const userTimeZone = getUserTimeZone();
    if (!canRegister) return;
    setCanRegister(false);
    setCanCancel(false);
    registerMutation.mutate({
      webinarId: webinarId,
      webinarCost: webinarCost,
      userTimeZone: userTimeZone,
    });
  };

  const handleCancelClick = (webinar: any) => {
    const webinarId = String(webinar.id);
    const userTimeZone = getUserTimeZone();
    if (!canCancel) return;
    setCanRegister(false);
    setCanCancel(false);
    cancelMutation.mutate({
      webinarId: webinarId,
      userTimeZone: userTimeZone,
    });
  };

  const handleWaitListClick = async () => {
    if (waitListButtonloading) return;
    setWaitListButtonloading(true);

    if (canBeAddedToWaitlist) {
      addToWaitListMutation.mutate({ contentId: id });
    } else {
      removeFromWaitListMutation.mutate({ contentId: id });
    }
  };

  useEffect(() => {
    if (
      webinar &&
      user?.availableCredits !== null &&
      user?.availableCredits !== undefined &&
      (webinar as WebinarContent).participants !== undefined
    ) {
      const now = new Date();
      const webinarStartDate = new Date((webinar as WebinarContent).startDate);
      const webinarEndDate = new Date((webinar as WebinarContent).endDate);
      const oneHourBefore = new Date(
        webinarStartDate.getTime() - 60 * 60 * 1000
      );

      const twentyFourHoursBefore = new Date(
        webinarStartDate.getTime() - 24 * 60 * 60 * 1000
      );

      const hasEnoughCredits =
        user?.availableCredits >= (webinar as WebinarContent).credits;
      const isInFuture = now < oneHourBefore;
      const maxParticipantsNotReached =
        (webinar as WebinarContent).participants <
        (webinar as WebinarContent).maxParticipants;

      if (maxParticipantsNotReached) {
        setMaxParticipantsReached(false);
      } else {
        setMaxParticipantsReached(true);
      }

      const isMoreThan24HoursBefore = now < twentyFourHoursBefore;
      const isRegistered = (webinar as WebinarContent).registered;
      const hasEnded = now > webinarEndDate;

      if (hasEnded) {
        setButtonText(
          isRegistered ? t("webinar-participated") : t("webinar-ended")
        );
        setCanRegister(false);
      } else if ((webinar as WebinarContent).canceled === true) {
        setButtonText(t("webinar-canceled"));
        setIsCanceled(true);
      } else if (
        (webinar as WebinarContent).participants >=
        (webinar as WebinarContent).maxParticipants
      ) {
        setButtonText(t("webinar-registration-max"));
        setCanRegister(false);
      } else if (!hasEnoughCredits && canRegister) {
        setButtonText(t("webinar-not-available-credits"));
        setCanRegister(false);
      } else if (!isInFuture && canRegister) {
        setButtonText(t("webinar-registration-closed"));
        setCanRegister(false);
      } else if (!isMoreThan24HoursBefore && isRegistered) {
        setButtonText(t("webinar-under-24-hours"));
        setCanRegister(false);
      } else {
        if ((webinar as WebinarContent).credits === 0) {
          setButtonText(
            t("webinar-register-zero", {
              placeholder: (webinar as WebinarContent).credits,
            })
          );
        } else if ((webinar as WebinarContent).credits === 1) {
          setButtonText(
            t("webinar-register-one", {
              placeholder: (webinar as WebinarContent).credits,
            })
          );
        } else if ((webinar as WebinarContent).credits > 1) {
          setButtonText(
            t("webinar-register-other", {
              placeholder: (webinar as WebinarContent).credits,
            })
          );
        }

        setCanRegister(true);
      }
      setCanCancel(isMoreThan24HoursBefore && isRegistered);
      setCanRegister(
        hasEnoughCredits &&
          isInFuture &&
          maxParticipantsNotReached &&
          !isRegistered
      );
    }
  }, [
    webinar,
    user?.availableCredits,
    (webinar as WebinarContent)?.participants,
  ]);

  if (isLoading || !webinar) {
    return (
      <div className="flex justify-center items-center h-screen">
        <CircularProgress style={{ color: "#DCAA00" }} />
      </div>
    );
  }

  return (
    <div className="px-6 sm:px-9 md:px-0 flex w-full h-full justify-center">
      <div className="w-full md:w-1/2">
        <h1 className="text-Yellow-default text-lg font-semibold my-4 md:my-9 ">
          {t("page-title")}
        </h1>
        <div className="max-w-full">
          <div className="mb-4">
            <div className=" z-0">
              {(webinar as WebinarContent)?.image ? (
                <img
                  className={`z-0`}
                  width={"100%"}
                  src={(webinar as WebinarContent)?.image}
                />
              ) : null}
            </div>

            {/* {(webinar as WebinarContent)?.hosts?.length ? (
              <div className="mt-[-1.8em]">
                <AuthorsDisplay
                  belowImage={true}
                  authors={(webinar as WebinarContent)?.hosts}
                />{" "}
              </div>
            ) : null} */}
          </div>
          <h1 className={`text-2xl font-bold mb-5`}>{webinar?.title}</h1>
          <div className="text-Grey text-xl mb-4">
            {(webinar as WebinarContent)?.teaserText}
          </div>
          {!(webinar as WebinarContent)?.image ? (
            <AuthorsDisplay
              belowImage={false}
              authors={[(webinar as WebinarContent)?.hosts]}
            />
          ) : null}
          <div className="flex">
            <div className="w-3/4 pr-4 flex flex-col space-y-2">
              <div className="text-md font-bold">
                {formatDateRange(
                  (webinar as WebinarContent)?.startDate,
                  (webinar as WebinarContent)?.endDate
                )}
                {(webinar as WebinarContent).registered && (
                  <CheckCircleOutlineIcon className="text-[#048273] ml-4 mb-1" />
                )}
              </div>
              <div className="text-md font-bold text-gray-400">
                {t("online")}
              </div>
              <p className="text-md text-Yellow-default font-bold">
                {((webinar as WebinarContent)?.hosts || [])
                  .map((host) => `${host.givenName} ${host.surname}`)
                  .join(", ")}
              </p>
            </div>

            <div className="hidden w-1/4 pl-4 lg:flex flex-col space-y-2">
              <div className="flex flex-col space-y-2">
                {!canCancel &&
                  !maxParticipantsReached &&
                  isCanceled !== true && (
                    <button
                      type="button"
                      className={`btn px-4 py-1.5 w-full ${
                        canRegister ? "bg-[#FDC300]" : "bg-[#E4E4E4]"
                      }`}
                      style={{
                        backgroundColor: canRegister ? "#FDC300" : "#E4E4E4",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRegisterClick(webinar);
                      }}
                      disabled={!canRegister}
                    >
                      {buttonText}
                    </button>
                  )}
                {canCancel &&
                  isCanceled !== true &&
                  (!maxParticipantsReached ||
                    (webinar as WebinarContent).registered) && (
                    <Button
                      variant={ButtonVariant.SECONDARY}
                      className="bg-[#757575] px-4 py-1.5 text-Black w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelClick(webinar);
                      }}
                    >
                      {t("webinar-cancel")}
                    </Button>
                  )}
                {maxParticipantsReached &&
                  isCanceled !== true &&
                  !(webinar as WebinarContent).registered && (
                    <>
                      <button
                        type="button"
                        className={`btn px-4 py-1.5 w-full border border-black ${
                          canBeAddedToWaitlist ? "bg-[#FFFFFF]" : "bg-[#FFFFFF]"
                        }`}
                        style={{
                          backgroundColor: canBeAddedToWaitlist
                            ? "#FFFFFF"
                            : "#FFFFFF",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleWaitListClick();
                        }}
                        disabled={waitListButtonloading}
                      >
                        {waitingListButtonText}
                      </button>
                    </>
                  )}
                {isCanceled === true && (
                  <>
                    <button
                      type="button"
                      className={
                        "btn px-4 py-1.5 w-full border border-black bg-[#FFFFFF]"
                      }
                      style={{
                        backgroundColor: "#FFFFFF",
                      }}
                      disabled={isCanceled}
                    >
                      {t("webinar-canceled")}
                    </button>
                  </>
                )}
              </div>
              <div className="mt-2">
                {successMessage && (
                  <div className="text-sm text-green-700">{successMessage}</div>
                )}
                {registerError && (
                  <div className="text-sm text-red-600">{registerError}</div>
                )}
                {cancelError && (
                  <div className="text-sm text-red-600">{cancelError}</div>
                )}
              </div>
            </div>
          </div>

          {/*new div to handle the buttons for mobile */}
          <div className="block lg:hidden mt-4">
            <div className="flex flex-col space-y-2">
              {!canCancel && !maxParticipantsReached && isCanceled !== true && (
                <button
                  type="button"
                  className={`btn px-4 py-1.5 w-full ${
                    canRegister ? "bg-[#FDC300]" : "bg-[#E4E4E4]"
                  }`}
                  style={{
                    backgroundColor: canRegister ? "#FDC300" : "#E4E4E4",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRegisterClick(webinar);
                  }}
                  disabled={!canRegister}
                >
                  {buttonText}
                </button>
              )}
              {canCancel &&
                isCanceled !== true &&
                (!maxParticipantsReached ||
                  (webinar as WebinarContent).registered) && (
                  <Button
                    variant={ButtonVariant.SECONDARY}
                    className="bg-[#757575] px-4 py-1.5 text-Black w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelClick(webinar);
                    }}
                  >
                    {t("webinar-cancel")}
                  </Button>
                )}
              {maxParticipantsReached &&
                isCanceled !== true &&
                !(webinar as WebinarContent).registered && (
                  <button
                    type="button"
                    className={`btn px-4 py-1.5 w-full border border-black ${
                      canBeAddedToWaitlist ? "bg-[#FFFFFF]" : "bg-[#FFFFFF]"
                    }`}
                    style={{
                      backgroundColor: canBeAddedToWaitlist
                        ? "#FFFFFF"
                        : "#FFFFFF",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleWaitListClick();
                    }}
                    disabled={waitListButtonloading}
                  >
                    {waitingListButtonText}
                  </button>
                )}
              {isCanceled === true && (
                <>
                  <button
                    type="button"
                    className={
                      "btn px-4 py-1.5 w-full border border-black bg-[#FFFFFF]"
                    }
                    style={{
                      backgroundColor: "#FFFFFF",
                    }}
                    disabled={isCanceled}
                  >
                    {t("webinar-canceled")}
                  </button>
                </>
              )}
            </div>
            <div className="mt-2">
              {successMessage && (
                <div className="text-sm text-green-700">{successMessage}</div>
              )}
              {registerError && (
                <div className="text-sm text-red-600">{registerError}</div>
              )}
              {cancelError && (
                <div className="text-sm text-red-600">{cancelError}</div>
              )}
            </div>
          </div>

          <Article content={webinar as WebinarContent} />
        </div>
      </div>
    </div>
  );
}
